import axios from './providers/axios';
import {convertBooleanValuesToNumbers} from "@/utils/utils";

const reportsService = {
    validParams: {
        types: ['users', 'selfAssessments', 'programs', 'gameLabs'],
        parameterTypes: ['users', 'properties'],
        periods: ['week', 'month', 'year'],
        status: [0, 1, 2]
    },

    getReportData(options = {}) {
        const defaults = {
            type: 'users',
            parameterType: 'users',
            page: 1,
            limit: 20,
            period: null,
            testIds: [],
            programIds: [],
            gameLabsIds: [],
            userIds: [],
            status: null,
            questionCountMin: null,
            questionCountMax: null,
            'created_at[start]': null,
            'created_at[end]': null,
            sortField: null,
            sortDirection: null
        };

        const params = { ...defaults, ...options };

        if (params.createdAtStart && !params['created_at[start]']) {
            params['created_at[start]'] = params.createdAtStart;
        }
        if (params.createdAtEnd && !params['created_at[end]']) {
            params['created_at[end]'] = params.createdAtEnd;
        }

        if (!this.validParams.types.includes(params.type)) {
            params.type = 'users';
        }

        if (!this.validParams.parameterTypes.includes(params.parameterType)) {
            params.parameterType = 'users';
        }

        if (params.period && !this.validParams.periods.includes(params.period)) {
            params.period = null;
        }

        if (params.status !== null && params.status !== undefined) {
            if (params.status === 'active') {
                params.status = 1;
            } else if (params.status === 'passive' || params.status === 'draft') {
                params.status = 0;
            } else if (params.status === 'all') {
                params.status = 2;
            }
        }

        let queryParams = [
            `type=${params.type}`,
            `parameterType=${params.parameterType}`,
        ];

        if (params.status !== null && params.status !== undefined && params.status !== 2 && params.status !== '2') {
            queryParams.push(`status=${params.status}`);
        }

        if (params.parameterType !== 'users') {
            queryParams.push(`page=${params.page}`);
            queryParams.push(`limit=${params.limit}`);
        } else {
        }

        if (params.period && this.validParams.periods.includes(params.period)) {
            queryParams.push(`period=${params.period}`);
        }

        if (params.testIds && Array.isArray(params.testIds) && params.testIds.length > 0) {
            params.testIds.forEach(id => {
                if (id && id !== 'all') {
                    queryParams.push(`testIds[]=${encodeURIComponent(id)}`);
                }
            });
        }

        if (params.programIds && Array.isArray(params.programIds) && params.programIds.length > 0) {
            params.programIds.forEach(id => {
                if (id && id !== 'all') {
                    queryParams.push(`programIds[]=${encodeURIComponent(id)}`);
                }
            });
        }

        if (params.gameLabsIds && Array.isArray(params.gameLabsIds) && params.gameLabsIds.length > 0) {
            params.gameLabsIds.forEach(id => {
                if (id && id !== 'all') {
                    queryParams.push(`gameLabsIds[]=${encodeURIComponent(id)}`);
                }
            });
        }

        if (params.userIds && Array.isArray(params.userIds) && params.userIds.length > 0) {
            params.userIds.forEach(id => {
                if (id && id !== 'all') {
                    queryParams.push(`userIds[]=${encodeURIComponent(id)}`);
                }
            });
        }

        if (params['questionCount[min]'] !== null && !isNaN(params['questionCount[min]'])) {
            queryParams.push(`questionCount[min]=${params['questionCount[min]']}`);
        } else if (params.questionCountMin !== null && !isNaN(params.questionCountMin)) {
            queryParams.push(`questionCount[min]=${params.questionCountMin}`);
        }

        if (params['questionCount[max]'] !== null && !isNaN(params['questionCount[max]'])) {
            queryParams.push(`questionCount[max]=${params['questionCount[max]']}`);
        } else if (params.questionCountMax !== null && !isNaN(params.questionCountMax)) {
            queryParams.push(`questionCount[max]=${params.questionCountMax}`);
        }

        if (params['completion[min]'] !== null && !isNaN(params['completion[min]'])) {
            queryParams.push(`completion[min]=${params['completion[min]']}`);
        } else if (params.completionMin !== null && !isNaN(params.completionMin)) {
            queryParams.push(`completion[min]=${params.completionMin}`);
        }

        if (params['completion[max]'] !== null && !isNaN(params['completion[max]'])) {
            queryParams.push(`completion[max]=${params['completion[max]']}`);
        } else if (params.completionMax !== null && !isNaN(params.completionMax)) {
            queryParams.push(`completion[max]=${params.completionMax}`);
        }

        if (params['created_at[start]']) {
            queryParams.push(`created_at[start]=${params['created_at[start]']}`);
        }
        if (params['created_at[end]']) {
            queryParams.push(`created_at[end]=${params['created_at[end]']}`);
        }

        if (params.sortField) {
            queryParams.push(`sort[field]=${params.sortField}`);

            if (params.sortDirection && ['asc', 'desc'].includes(params.sortDirection.toLowerCase())) {
                queryParams.push(`sort[direction]=${params.sortDirection}`);
            }
        }

        const url = `v1/admin/report/store?${queryParams.join('&')}`;

        return axios.get(url)
            .then(response => {
                return response;
            })
            .catch(error => {
                return Promise.reject(error);
            });
    },

    getUserReports(options = {}) {
        return this.getReportData({
            type: 'users',
            parameterType: 'users',
            ...options
        });
    },

    getTestReports(options = {}) {
        return this.getReportData({
            type: 'selfAssessments',
            parameterType: 'users',
            ...options
        });
    },

    getProgramReports(options = {}) {
        return this.getReportData({
            type: 'programs',
            parameterType: 'users',
            ...options
        });
    },

    getGamelabReports(options = {}) {
        return this.getReportData({
            type: 'gameLabs',
            parameterType: 'users',
            ...options
        });
    },

    getReportDataSimple(options) {
        return axios.get('/api/v1/admin/report/store', {
            params: options,
            timeout: 10000
        })
            .then(response => {
                return response;
            })
            .catch(error => {
                return Promise.reject(error);
            });
    },

    getTestReportsSimple(options) {
        return this.getReportDataSimple({
            ...options,
            type: 'selfAssessments',
            parameterType: 'users'
        });
    },

    getProgramReportsSimple(options) {
        return this.getReportDataSimple({
            ...options,
            type: 'programs',
            parameterType: 'users'
        });
    },

    getUserReportsSimple(options) {
        return this.getReportDataSimple({
            ...options,
            type: 'users',
            parameterType: 'users'
        });
    },

    getGamelabReportsSimple(options) {
        return this.getReportDataSimple({
            ...options,
            type: 'gameLabs',
            parameterType: 'users'
        });
    },

    buildQueryString(options) {
        const params = { ...options };
        const queryParams = [];

        Object.keys(params).forEach(key => {
            if (params[key] !== null && params[key] !== undefined) {
                if (Array.isArray(params[key])) {
                    params[key].forEach(item => {
                        queryParams.push(`${key}[]=${encodeURIComponent(item)}`);
                    });
                } else {
                    queryParams.push(`${key}=${encodeURIComponent(params[key])}`);
                }
            }
        });

        return queryParams.join('&');
    },

    testConnection() {
        return this.getUserReports({
            period: 'week'
        })
            .then(() => {
                return true;
            })
            .catch(error => {
                return false;
            });
    },

    validateParams(params) {
        const errors = [];

        if (params.type && !this.validParams.types.includes(params.type)) {
            errors.push(`Geçersiz type: ${params.type}`);
        }

        if (params.parameterType && !this.validParams.parameterTypes.includes(params.parameterType)) {
            errors.push(`Geçersiz parameterType: ${params.parameterType}`);
        }

        if (params.period && !this.validParams.periods.includes(params.period)) {
            errors.push(`Geçersiz period: ${params.period}`);
        }

        if (params.status !== null && params.status !== undefined && !this.validParams.status.includes(Number(params.status))) {
            errors.push(`Geçersiz status: ${params.status}`);
        }

        return {
            isValid: errors.length === 0,
            errors: errors
        };
    },

    getSavedReports(options = {}) {
        const defaults = {
            type: null,
            page: 1,
            limit: 20,
            period: null,
            filters: []
        };

        const params = { ...defaults, ...options };

        let queryParams = [];

        queryParams.push(`page=${params.page}`);
        queryParams.push(`limit=${params.limit}`);

        Object.keys(params)
            .filter(key => key !== 'filters' && key !== 'page' && key !== 'limit' && params[key] !== null && params[key] !== undefined && params[key] !== '')
            .forEach(key => {
                queryParams.push(`${key}=${encodeURIComponent(params[key])}`);
            });

        if (params.filters && params.filters.length > 0) {
            const tempFilters = params.filters.map((filter) => {
                return `${filter.key}=${filter.value}`;
            });
            queryParams = queryParams.concat(tempFilters);
        }

        const url = `v1/admin/report/saved/store${queryParams.length > 0 ? '?' + queryParams.join('&') : ''}`;

        return axios.get(url)
            .then(response => {
                return response;
            })
            .catch(error => {
                return Promise.reject(error);
            });
    },

    createSavedReport(pdfFile, reportName, reportType) {
        const formData = new FormData();
        formData.append('name', reportName);
        formData.append('type', reportType);
        formData.append('pdfUrl', pdfFile);

        return axios.post('/v1/admin/report/saved/add', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }
}

export default reportsService;
